import React from 'react';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import { IonButton } from '@ionic/react';
import { useSelector } from 'react-redux';
import { SelectOption } from '../../models/selectOption';
import { FormattedMessage, useIntl } from 'react-intl';
import { JobMessages } from '../../intl/JobMessages';
import { ControllerMessages } from '../../intl/ControllerMessages';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { GenericMessages } from 'src/intl/GenericMessages';
import { InputSelect } from '../reusable-components/InputSelect';
import { Input } from '../reusable-components/Input';

interface JobUploadFormProps {
  logFiles: SelectOption[];
  controllerName?: string;
  submitForm: (
    logFileName: string,
    orgId: string,
    siteName: string,
    description: string,
    heatExchangerModelNumber: string,
  ) => Promise<void>;
}

interface JobUploadFormFields {
  logFileName: string;
  siteName: string;
  description: string;
  heatExchangerModelNumber: string;
  controllerName?: string;
}

const validationSchema = yup.object({
  logFileName: yup.string().required('A log file is required'),
  siteName: yup.string().required('A site name is required'),
  controllerName: yup.string().required('Controller name is required'),
});

const JobUploadForm: React.FC<JobUploadFormProps> = (props: JobUploadFormProps) => {
  const intl = useIntl();

  /**
   * Manages state of the user data.
   */
  const userData = useSelector(selectAuthUserInfo);

  const submitForm = async (formValues: JobUploadFormFields) => {
    if (!userData) {
      console.error('No user info exists!');
      return;
    }
    await props.submitForm(
      formValues.logFileName,
      userData.org,
      formValues.siteName,
      formValues.description,
      formValues.heatExchangerModelNumber,
    );
  };

  const selectLogPlaceholder = intl.formatMessage(JobMessages.uploadFormSelectLogPlaceholder);
  const siteNamePlaceholder = intl.formatMessage(JobMessages.uploadFormSiteNamePlaceholder);
  const descriptionLabel = intl.formatMessage(JobMessages.uploadFormDescriptionLabel);
  const descriptionPlaceholder = intl.formatMessage(JobMessages.uploadFormDescriptionPlaceholder);
  const hxLabel = intl.formatMessage(JobMessages.uploadFormHXModelNumberLabel);
  const hxPlaceholder = intl.formatMessage(JobMessages.uploadFormHXModelNumberPlaceholder);
  const controllerLabel = intl.formatMessage(ControllerMessages.controller);

  const validate = React.useCallback(
    (values: JobUploadFormFields) => {
      return !!values.description ? {} : { description: intl.formatMessage(GenericMessages.fieldRequiredLabel) };
    },
    [intl],
  );

  return (
    <Formik
      initialValues={{
        logFileName: '',
        siteName: '',
        description: '',
        heatExchangerModelNumber: '',
        controllerName: props.controllerName,
      }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validate={validate}
      validateOnChange={true}
      onSubmit={(values: JobUploadFormFields) => submitForm(values)}
    >
      {(formikProps: FormikProps<JobUploadFormFields>) => (
        <div className="job-upload-form">
          <form onSubmit={formikProps.handleSubmit}>
            <InputSelect
              label={intl.formatMessage(JobMessages.uploadFormLogFileLabel)}
              placeholder={selectLogPlaceholder}
              options={props.logFiles.map((logFile: SelectOption) => ({
                value: logFile._id,
                label: logFile.name,
              }))}
              value={formikProps.values.logFileName}
              onChange={e => {
                formikProps.setFieldValue('logFileName', e);
                formikProps.setFieldValue('description', e.replace(/\.(log)$/i, ''));
              }}
              onBlur={formikProps.handleBlur}
              name="logFileName"
              id="job-upload-log-file"
              errorText={formikProps.touched.logFileName ? formikProps.errors.logFileName : undefined}
              required
            />

            <Input
              label={intl.formatMessage(JobMessages.uploadFormSiteNameLabel)}
              value={formikProps.values.siteName}
              placeholder={siteNamePlaceholder}
              onChange={formikProps.handleChange}
              onBlur={formikProps.handleBlur}
              name="siteName"
              id="job-upload-site-name"
              errorText={formikProps.touched.siteName ? formikProps.errors?.siteName : undefined}
              required
            />

            <Input
              label={descriptionLabel}
              value={formikProps.values.description}
              placeholder={descriptionPlaceholder}
              onChange={formikProps.handleChange}
              name="description"
              id="job-upload-description"
            />

            <Input
              label={hxLabel}
              value={formikProps.values.heatExchangerModelNumber}
              placeholder={hxPlaceholder}
              onChange={formikProps.handleChange}
              name="heatExchangerModelNumber"
              id="job-upload-hx-model-number"
            />

            <Input
              label={controllerLabel}
              value={formikProps.values.controllerName}
              onChange={formikProps.handleChange}
              name="controllerName"
              disabled={true}
              id="job-upload-controller-name"
            />

            <div className="job-upload-footer">
              <IonButton expand="block" size="large" type="submit">
                <FormattedMessage {...JobMessages.uploadJobButtonLabel} />
              </IonButton>
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default JobUploadForm;
